import { BaseProduct } from '../types';
import { getHexColorCode } from './getHexColorCode';

const imageBase =
  process.env.NODE_ENV === 'development'
    ? '/images'
    : process.env.REACT_APP_IMAGE_BASE;

type GetColourTag = (product: BaseProduct) => string | void;
type GetColourFromColourTag = (tag: string) => string;
type ProductHasColour = (colour: string) => (product: BaseProduct) => boolean;
type ColourOptns = {
  name: string;
  code: string;
  '25mm_swatch_url': string;
  '45mm_swatch_url': string;
  featured_image: string;
  colour_discount_code: string;
}[];
type GetColourOptions = (products: BaseProduct[]) => ColourOptns;
type GetDayColourFromColour = (name: string) => string;
type ProductHasDayColour = (
  dayColour: string,
) => (product: BaseProduct) => boolean;

const colorNameTAG = 'Colour: ';
const colorCodeTAG = 'Hex Code: ';

export const getColourOptions: GetColourOptions = products => {
  return products.reduce((accumulator, product) => {
    const colourTag = getColourTag(product);
    if (!colourTag) return accumulator;
    const colourCode = getColourCode(product);
    if (!colourCode) return accumulator;
    const isUnique = !accumulator.some(color => color.name === colourTag);
    const featuredImage = `${product.images[0]}`;
    if (!isUnique) return accumulator;
    return accumulator.concat({
      name: colourTag,
      code: colourCode,
      '25mm_swatch_url': '',
      '45mm_swatch_url': '',
      featured_image: featuredImage,
      colour_discount_code: `${product.metafields.colour_discount_code || "0%"}`
    });
  }, [] as ColourOptns);
};

export const getDayColourOptions: GetColourOptions = products => {
  return products.reduce((accumulator, product) => {
    if (!product?.metafields?.day_colour) return accumulator;
    product.metafields.day_colour.forEach(option => {
      const isUnique = !accumulator.some(color => color.name === option);
      if (!isUnique) return;
      accumulator.push({
        name: option,
        code: '',
        '25mm_swatch_url': '',
        '45mm_swatch_url': '',
        featured_image: '',
        colour_discount_code: ''
      });
    });
    if (product?.metafields?.day_colour_hex) {
      product.metafields.day_colour_hex.forEach(option => {
        let optionStart = option.split('(')[0];
        accumulator.forEach((value, index) => {
          if (value.name.startsWith(optionStart) === true)
            accumulator[index].code = getColourHexCode(option);
        });
      });
    }

    if (product?.metafields?.day_colour_25mm_swatch_url) {
      product.metafields.day_colour_25mm_swatch_url.forEach(option => {
        let optionStart = option.split('(')[0];
        accumulator.forEach((value, index) => {
          if (value.name.startsWith(optionStart) === true)
            accumulator[index]['25mm_swatch_url'] = getDayColourSwatchUrl(
              option,
            );
        });
      });
    }

    if (product?.metafields?.day_colour_45mm_swatch_url) {
      product.metafields.day_colour_45mm_swatch_url.forEach(option => {
        let optionStart = option.split('(')[0];
        accumulator.forEach((value, index) => {
          if (value.name.startsWith(optionStart) === true)
            accumulator[index]['45mm_swatch_url'] = getDayColourSwatchUrl(
              option,
            );
        });
      });
    }

    return accumulator;
  }, [] as ColourOptns);
};

export const getColourTag: GetColourTag = ({ tags }) =>
  tags.find(tag => tag.includes(colorNameTAG) && !tag.includes('Bracket'));

export const getColourCode: GetColourTag = ({ tags }) =>
  tags.find(tag => tag.includes(colorCodeTAG))?.replace(colorCodeTAG, '');
export const getColourHexCode = (colourWithHex: string) => {
  if (!colourWithHex) return '';
  const hexCode = colourWithHex.match(/\(([^)]+)\)/);
  return hexCode !== null ? hexCode[1] : '';
};

export const getDayColourSwatchUrl = (colourWithSwatchUrl: string) => {
  if (!colourWithSwatchUrl) return '';
  const swatchUrl = colourWithSwatchUrl.match(/\(([^)]+)\)/);
  return swatchUrl !== null ? swatchUrl[1] : '';
};

export const getColourFromColourTag: GetColourFromColourTag = (tag = '') =>
  tag.split(colorNameTAG).pop() || '';

export const getColourCodeFromColourTag: GetColourFromColourTag = (tag = '') =>
  getHexColorCode(getColourFromColourTag(tag));

export const getDayColourNameFromColour: GetDayColourFromColour = (name = '') =>
  name.split('(').shift() || '';

export const productHasColour: ProductHasColour = colour => product =>
  product.tags.some(tag => tag === colour);

export const productHasDayColour: ProductHasDayColour = dayColour => ({
  metafields: { day_colour },
}) => {
  if (!day_colour) return true;
  return day_colour.some(option => option === dayColour);
};
